import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
} from '@angular/core'
import { AbstractControl, FormArray, FormGroup, NgControl } from '@angular/forms'
import { BaseControlValueAccessor } from '@shared/base-control-value-acessor'
import { EventService } from '@shared/services/event/event.service'
import { FormHelperService } from '@widgets/form/services/form-helper.service'
import { Subscription } from 'rxjs'
import { DistribuicaoBolsas, distribuicaoBolsasReference } from './distribuicao-bolsas.model'
import { distribuicaoBolsasValidator } from './distribuicao-bolsas.validator'

export const EVENT_SUBMIT_DISTRIBUICAO_BOLSAS = 'eventSubmitDistribuicaoBolsas'

@Component({
  selector: 'app-distribuicao-bolsas',
  templateUrl: './distribuicao-bolsas.component.html',
  styleUrls: ['./distribuicao-bolsas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DistribuicaoBolsasComponent
  extends BaseControlValueAccessor<DistribuicaoBolsas[]>
  implements OnInit, OnDestroy, DoCheck {
  form: FormGroup
  validator = distribuicaoBolsasValidator
  @Input()
  isAvaliacao = false

  columns = [
    {
      columnDef: 'categoriaProfissional',
      header: 'Categoria profissional',
      cell: (distribuicao: DistribuicaoBolsas) => `${distribuicao.categoriaProfissional}`,
    },
    {
      columnDef: 'numeroBolsasSolicitadas',
      header: 'N° de bolsas solicitadas',
      cell: (distribuicao: DistribuicaoBolsas) => this.formatNumeroBolsas(distribuicao.numeroBolsasSolicitadas),
    },
    {
      columnDef: 'numeroBolsasAprovadas',
      header: 'N° de bolsas aprovadas',
      cell: (distribuicao: DistribuicaoBolsas) => this.formatNumeroBolsas(distribuicao.numeroBolsasAprovadas),
    },
  ]

  control: AbstractControl

  displayedColumns = this.columns.map((column) => column.columnDef)

  private subscriptions$ = new Subscription()

  constructor(
    @Self() @Optional() public ngControl: NgControl,
    private formHelperService: FormHelperService,
    private changeDetectorRef: ChangeDetectorRef,
    private eventService: EventService
  ) {
    super()
    this.buildForm()
    if (this.ngControl) {
      this.ngControl.valueAccessor = this
    }
  }

  writeValue(distribuicoes: DistribuicaoBolsas[]): void {
    this.formArray?.clear()
    distribuicoes?.forEach((distribuicao) => this.addToFormArray(distribuicao))
    this.value = distribuicoes
    this.changeDetectorRef.markForCheck()
  }

  registerOnChange(fn: any): void {
    super.registerOnChange(fn)
    this.form.valueChanges.subscribe((value) => {
      if (!this.disabled) {
        this.ensureRanges(value?.distribuicoes)
        this.onChange(this.form.controls.distribuicoes.value)
      }
    })
  }

  getNumeroBolsasAprovadas(distribuicao: DistribuicaoBolsas) {
    if (distribuicao.numeroBolsasAprovadas < 0) {
      return 0
    }

    if (this.isAvaliacao && distribuicao.numeroBolsasAprovadas > distribuicao.numeroBolsasSolicitadas) {
      return distribuicao.numeroBolsasSolicitadas
    }

    return distribuicao.numeroBolsasAprovadas
  }

  ensureRanges(distribuicoes: DistribuicaoBolsas[]) {
    const newDistribuicoes = distribuicoes.map((dist) => ({
      ...dist,
      numeroBolsasAprovadas: this.getNumeroBolsasAprovadas(dist),
    }))

    if (JSON.stringify(newDistribuicoes) !== JSON.stringify(distribuicoes)) {
      this.form.controls.distribuicoes.patchValue(newDistribuicoes, { emitEvent: false })
    }
  }

  touchAll() {
    this.formHelperService.touchAll(this.form)
    this.control.markAsTouched()
    this.changeDetectorRef.markForCheck()
  }

  ngDoCheck(): void {
    if (!this.form.valid) {
      this.control?.markAsTouched()
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeAll()
  }

  ngOnInit(): void {
    this.control = this.ngControl?.control
    this.subscribeToEvents()
  }

  subscribeToEvents = () => {
    if (this.disabled) {
      return
    }
    this.subscriptions$.add(
      this.eventService.observe(EVENT_SUBMIT_DISTRIBUICAO_BOLSAS).subscribe((_) => this.touchAll())
    )
  }

  unsubscribeAll() {
    this.subscriptions$.unsubscribe()
  }

  isEditableCell = (cell: { columnDef: string }) => !this.disabled && cell?.columnDef === 'numeroBolsasAprovadas'

  canShowError(controlIndex: number) {
    const control = this.formArray.at(controlIndex).get('numeroBolsasAprovadas')
    return control?.touched && control?.errors
  }

  get formArray() {
    return this.form?.controls?.distribuicoes as FormArray
  }

  private buildForm() {
    const fb = this.formHelperService.formBuilder
    this.form = fb.group({ distribuicoes: fb.array([]) })
  }

  private addToFormArray(distribuicao: DistribuicaoBolsas) {
    const formGroup = this.formHelperService.createForm(distribuicaoBolsasReference, distribuicao)
    this.formArray.push(formGroup)
  }

  onTouched() {
    super.onTouched()
    this.changeDetectorRef.markForCheck()
  }

  onReplicarValores = () => {
    const oldDistribuicoes = this.formArray.value as DistribuicaoBolsas[]
    const newDistribuicoes = oldDistribuicoes?.map((distribuicao) => ({
      ...distribuicao,
      numeroBolsasAprovadas: distribuicao.numeroBolsasSolicitadas,
    }))
    this.form.controls.distribuicoes.setValue(newDistribuicoes)
  }

  setDisabledState(isDisabled: boolean) {
    const oldState = this.disabled
    super.setDisabledState(isDisabled)
    if (oldState !== this.disabled) {
      this.writeValue(this.formArray.getRawValue())
    }
  }

  getFootCellValue(column) {
    if (column.columnDef === 'categoriaProfissional') {
      return 'Total de bolsas aprovadas'
    }

    if (['numeroBolsasSolicitadas', 'numeroBolsasAprovadas'].includes(column.columnDef)) {
      return this.getColumnSum(column)
    }

    return ''
  }

  getColumnSum(column) {
    const sum =
      this.formArray
        ?.getRawValue()
        ?.reduce((acc, distribuicaoBolsa) => acc + +distribuicaoBolsa[column.columnDef], 0) || 0
    return this.formatNumeroBolsas(sum)
  }

  private formatNumeroBolsas(number: number) {
    return `${number}`.padStart(2, '0')
  }
}
